import { createFileRoute } from '@tanstack/react-router';
import { Mail, MessageSquareText, ChevronDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { usePageTitle } from '../components/usePageTitle.js';
import { signupUrl } from '../lib/urls.js';

export const Route = createFileRoute('/notifications')({
  component: NotificationsPage,
});

// ─── Demo constants ───────────────────────────────────────────────────────────
const BNAME = 'Sunshine Imaging';
const ADDR = 'Level 2, 45 Miller St, North Sydney NSW 2060';
const CLIENT = 'James';
const CLIENT_EMAIL = 'james.okafor@example.com';
const STAFF_1 = 'Dr. Sarah Chen';
const STAFF_2 = 'Dr. Marcus Thompson';
const SERVICE = 'CT Scan';
const DATE_LONG = 'Wednesday, 18 June 2026 at 09:30';
const DATE_SHORT = 'Wed 18 Jun at 09:30';
const DATE_NEW_LONG = 'Friday, 20 June 2026 at 11:00';
const DATE_NEW_SHORT = 'Fri 20 Jun at 11:00';
const MANAGE_URL = 'https://app.bookaburra.com/m/j9kx3p';

// ─── Shared components ────────────────────────────────────────────────────────

type Tier = 'all' | 'small_plus' | 'large_only';

function TierPill({ tier }: { tier: Tier }) {
  if (tier === 'all') {
    return (
      <span className="whitespace-nowrap rounded-full bg-brand-sage/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-brand-sage">
        All plans
      </span>
    );
  }
  if (tier === 'small_plus') {
    return (
      <span className="whitespace-nowrap rounded-full bg-brand-gold/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-brand-ink-soft">
        Small business +
      </span>
    );
  }
  return (
    <span className="whitespace-nowrap rounded-full bg-brand-moss/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-brand-moss">
      Large business
    </span>
  );
}

// ─── Email mock ───────────────────────────────────────────────────────────────

function EmailFrame({
  subject,
  children,
}: {
  subject: string;
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 text-sm shadow-sm">
      {/* Email client header bar */}
      <div className="space-y-1.5 border-b border-zinc-200 bg-zinc-100 px-4 py-3 text-xs">
        <div className="text-zinc-600">
          <span className="font-medium">From</span>{' '}
          <span className="font-semibold text-zinc-800">{BNAME} – Bookaburra Booking</span>{' '}
          <span className="text-zinc-400">&lt;hello@bookaburra.com&gt;</span>
        </div>
        <div className="text-zinc-600">
          <span className="font-medium">To</span>{' '}
          <span className="text-zinc-700">{CLIENT_EMAIL}</span>
        </div>
        <div className="font-semibold text-zinc-900">{subject}</div>
      </div>

      {/* Email body */}
      <div className="bg-zinc-50 p-4">
        <div className="mx-auto max-w-sm overflow-hidden rounded-lg">
          {/* Brand header */}
          <div className="bg-brand-ink-soft px-6 py-5">
            <p className="text-xl font-extrabold tracking-tight text-brand-cream">{BNAME}</p>
            <p className="mt-0.5 text-xs text-brand-cream/60">via Bookaburra</p>
          </div>
          {/* Content */}
          <div className="bg-white px-6 py-6 leading-relaxed text-zinc-800">
            {children}
          </div>
          {/* Footer */}
          <div className="border-t border-zinc-100 bg-zinc-50 px-6 py-4 text-center text-[11px] text-zinc-400">
            <p>{BNAME} · Powered by Bookaburra</p>
            <p className="mt-0.5">Reply to this email and {BNAME} will receive it directly.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 border-b border-zinc-100 py-1.5 last:border-0 text-sm">
      <span className="w-14 shrink-0 text-zinc-500">{label}</span>
      <span className="font-medium text-zinc-800">{value}</span>
    </div>
  );
}

function EmailCta({ href, children }: { href: string; children: ReactNode }) {
  return (
    <div className="mt-5">
      <a
        href={href}
        className="inline-block rounded-lg bg-brand-sage px-5 py-2.5 text-sm font-semibold text-white"
      >
        {children}
      </a>
    </div>
  );
}

// ─── SMS mock ─────────────────────────────────────────────────────────────────

function SmsMock({ lines }: { lines: string[] }) {
  return (
    <div className="mx-auto w-52">
      <div className="overflow-hidden rounded-[2rem] border-[3px] border-zinc-300 bg-zinc-200 shadow-md">
        {/* Status bar */}
        <div className="bg-zinc-300 px-5 pb-2 pt-3">
          <p className="text-center text-[9px] font-bold text-zinc-600">Bookaburra</p>
        </div>
        {/* Messages */}
        <div className="min-h-[160px] space-y-3 bg-white px-3 py-4">
          {lines.map((line, i) => (
            <div key={i} className="flex justify-start">
              <div className="max-w-[90%] rounded-2xl rounded-bl-sm bg-zinc-100 px-3 py-2 text-[11px] leading-snug text-zinc-800 whitespace-pre-line">
                {line}
              </div>
            </div>
          ))}
        </div>
        {/* Home indicator */}
        <div className="flex justify-center bg-white pb-3">
          <div className="h-1 w-12 rounded-full bg-zinc-300" />
        </div>
      </div>
    </div>
  );
}

// ─── Notification item type ───────────────────────────────────────────────────

type NotifItem = {
  id: string;
  title: string;
  trigger: string;
  preview: string;
  tier: Tier;
  body: ReactNode;
};

// ─── Mail-client split component ──────────────────────────────────────────────

function NotifSplitView({
  items,
  sectionId,
}: {
  items: NotifItem[];
  sectionId: string;
}) {
  const [selectedId, setSelectedId] = useState(items[0]?.id ?? '');

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:items-start">
      {/* LEFT: nav list — sticky on desktop, accordion on mobile */}
      <div className="lg:sticky lg:top-6">
        <ul className="space-y-2 lg:rounded-2xl lg:border lg:border-brand-sage/15 lg:bg-white lg:p-2 lg:shadow-[0_30px_80px_-30px_rgba(19,33,36,0.06)]">
          {items.map((item) => {
            const isSelected = item.id === selectedId;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  id={`${sectionId}-nav-${item.id}`}
                  aria-controls={`${sectionId}-panel-${item.id}`}
                  aria-expanded={isSelected}
                  onClick={() =>
                    setSelectedId((current) =>
                      // On mobile, allow collapsing the open row by clicking again.
                      // On desktop the click always switches to the new row.
                      current === item.id ? current : item.id,
                    )
                  }
                  className={`flex w-full items-start justify-between gap-3 rounded-xl border px-4 py-3 text-left transition ${
                    isSelected
                      ? 'border-brand-sage/30 bg-brand-cream-shadow shadow-[inset_0_0_0_1px_rgba(124,151,127,0.08)]'
                      : 'border-brand-sage/15 bg-white hover:border-brand-sage/25 hover:bg-brand-cream-shadow/60'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3
                        className={`truncate text-sm font-semibold ${
                          isSelected ? 'text-brand-sage' : 'text-brand-ink-soft'
                        }`}
                      >
                        {item.title}
                      </h3>
                    </div>
                    <p className="mt-0.5 truncate text-xs text-brand-ink-soft/55">
                      {item.preview}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <TierPill tier={item.tier} />
                    <ChevronDown
                      className={`h-4 w-4 text-brand-ink-soft/40 transition-transform lg:hidden ${
                        isSelected ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>

                {/* Mobile: inline expanded panel underneath the row */}
                <div
                  id={`${sectionId}-panel-${item.id}-mobile`}
                  role="region"
                  aria-labelledby={`${sectionId}-nav-${item.id}`}
                  className={`lg:hidden ${isSelected ? '' : 'hidden'}`}
                >
                  <div className="mt-3 rounded-2xl border border-brand-sage/15 bg-white p-4 shadow-[0_30px_80px_-30px_rgba(19,33,36,0.06)]">
                    <p className="mb-3 text-xs text-brand-ink-soft/55">{item.trigger}</p>
                    {item.body}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* RIGHT: desktop expanded preview — all panels in DOM, only one visible */}
      <div className="hidden lg:block">
        {items.map((item) => {
          const isSelected = item.id === selectedId;
          return (
            <div
              key={item.id}
              id={`${sectionId}-panel-${item.id}`}
              role="region"
              aria-labelledby={`${sectionId}-nav-${item.id}`}
              aria-hidden={!isSelected}
              className={isSelected ? '' : 'hidden'}
            >
              <div className="rounded-2xl border border-brand-sage/15 bg-white p-6 shadow-[0_30px_80px_-30px_rgba(19,33,36,0.06)] sm:p-8">
                <div className="mb-5 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-bold text-brand-ink-soft">{item.title}</h3>
                    <p className="mt-1 text-xs text-brand-ink-soft/55">{item.trigger}</p>
                  </div>
                  <TierPill tier={item.tier} />
                </div>
                <div className="mx-auto max-w-[600px]">{item.body}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function NotificationsPage() {
  usePageTitle('Notification examples');

  const emailItems: NotifItem[] = [
    {
      id: 'booking-confirmation',
      title: 'Booking confirmation',
      trigger: `Sent immediately when a client books a ${SERVICE}`,
      preview: `You're booked in — ${SERVICE} on ${DATE_SHORT}`,
      tier: 'all',
      body: (
        <EmailFrame subject={`${SERVICE} confirmed — ${BNAME}, 18 Jun`}>
          <p>Hi {CLIENT},</p>
          <p className="mt-3 text-base font-semibold text-brand-ink-soft">You&rsquo;re booked in.</p>
          <div className="mt-4 divide-y divide-zinc-100 rounded-lg border border-zinc-200">
            <Detail label="Service" value={SERVICE} />
            <Detail label="When" value={DATE_LONG} />
            <Detail label="Where" value={ADDR} />
            <Detail label="Staff" value={STAFF_1} />
          </div>
          <EmailCta href={MANAGE_URL}>Manage my booking</EmailCta>
        </EmailFrame>
      ),
    },
    {
      id: 'reminder-24h',
      title: '24-hour reminder',
      trigger: 'Sent the afternoon before the booking',
      preview: `Reminder about tomorrow's ${SERVICE} at 09:30`,
      tier: 'all',
      body: (
        <EmailFrame subject={`Reminder: ${SERVICE} tomorrow at 09:30 — ${BNAME}`}>
          <p>Hi {CLIENT},</p>
          <p className="mt-3 text-zinc-700">
            Just a reminder about tomorrow&rsquo;s booking.
          </p>
          <div className="mt-4 divide-y divide-zinc-100 rounded-lg border border-zinc-200">
            <Detail label="Service" value={SERVICE} />
            <Detail label="When" value={DATE_LONG} />
            <Detail label="Where" value={ADDR} />
          </div>
          <EmailCta href={MANAGE_URL}>Reschedule if needed</EmailCta>
        </EmailFrame>
      ),
    },
    {
      id: 'reminder-1h',
      title: '1-hour reminder',
      trigger: 'Sent one hour before the booking',
      preview: `Your ${SERVICE} starts in about an hour`,
      tier: 'all',
      body: (
        <EmailFrame subject={`Starting in an hour: ${SERVICE} at 09:30 — ${BNAME}`}>
          <p>Hi {CLIENT},</p>
          <p className="mt-3 text-zinc-700">
            Your {SERVICE} at {BNAME} starts in about an hour.
          </p>
          <div className="mt-4 divide-y divide-zinc-100 rounded-lg border border-zinc-200">
            <Detail label="When" value="Today at 09:30" />
            <Detail label="Where" value={ADDR} />
          </div>
          <EmailCta href={MANAGE_URL}>View my booking</EmailCta>
        </EmailFrame>
      ),
    },
    {
      id: 'rescheduled',
      title: 'Booking rescheduled',
      trigger: 'Sent when a booking is moved to a new time',
      preview: `New time: ${DATE_NEW_SHORT}`,
      tier: 'all',
      body: (
        <EmailFrame subject={`Your ${SERVICE} has moved — new time: ${DATE_NEW_SHORT}`}>
          <p>Hi {CLIENT},</p>
          <p className="mt-3 text-zinc-700">Your booking has been rescheduled.</p>
          <div className="mt-4 divide-y divide-zinc-100 rounded-lg border border-zinc-200">
            <Detail label="New time" value={DATE_NEW_LONG} />
            <Detail label="Was" value={DATE_LONG} />
            <Detail label="Where" value={ADDR} />
          </div>
          <EmailCta href={MANAGE_URL}>View updated booking</EmailCta>
        </EmailFrame>
      ),
    },
    {
      id: 'cancelled',
      title: 'Booking cancelled',
      trigger: 'Sent when a booking is cancelled by staff or the client',
      preview: `Your ${SERVICE} on ${DATE_SHORT} has been cancelled`,
      tier: 'all',
      body: (
        <EmailFrame subject={`${SERVICE} cancelled — ${BNAME}`}>
          <p>Hi {CLIENT},</p>
          <p className="mt-3 text-zinc-700">
            Your {SERVICE} on {DATE_LONG} has been cancelled.
          </p>
          <p className="mt-3 text-zinc-700">
            If you&rsquo;d like to book another time, use the link below.
          </p>
          <EmailCta href={MANAGE_URL}>Rebook a {SERVICE}</EmailCta>
        </EmailFrame>
      ),
    },
    {
      id: 'pending-approval',
      title: 'Pending approval',
      trigger: 'Sent when the business has approval mode turned on',
      preview: 'Booking request received — we’ll confirm shortly',
      tier: 'all',
      body: (
        <EmailFrame subject={`Booking request received — ${BNAME} will confirm shortly`}>
          <p>Hi {CLIENT},</p>
          <p className="mt-3 text-zinc-700">
            Your booking request for a {SERVICE} on {DATE_LONG} has been
            received. It&rsquo;s pending approval and we&rsquo;ll send you
            a confirmation within 24 hours.
          </p>
        </EmailFrame>
      ),
    },
    {
      id: 'approved',
      title: 'Booking approved',
      trigger: 'Sent when a business approves a pending booking',
      preview: 'Your booking is confirmed',
      tier: 'all',
      body: (
        <EmailFrame subject={`Confirmed: ${SERVICE} on 18 Jun — ${BNAME}`}>
          <p>Hi {CLIENT},</p>
          <p className="mt-3 text-base font-semibold text-brand-ink-soft">
            Your booking is confirmed.
          </p>
          <div className="mt-4 divide-y divide-zinc-100 rounded-lg border border-zinc-200">
            <Detail label="Service" value={SERVICE} />
            <Detail label="When" value={DATE_LONG} />
            <Detail label="Where" value={ADDR} />
          </div>
          <EmailCta href={MANAGE_URL}>Manage my booking</EmailCta>
        </EmailFrame>
      ),
    },
    {
      id: 'staff-reassigned',
      title: 'Staff reassignment',
      trigger: 'Sent after a sick-day reshuffle assigns a new staff member',
      preview: `Now with ${STAFF_2}`,
      tier: 'large_only',
      body: (
        <EmailFrame subject={`Update: your ${SERVICE} has a new staff member`}>
          <p>Hi {CLIENT},</p>
          <p className="mt-3 text-zinc-700">
            Your {SERVICE} on {DATE_LONG} will now be with a different
            staff member.
          </p>
          <div className="mt-4 divide-y divide-zinc-100 rounded-lg border border-zinc-200">
            <Detail label="New staff" value={STAFF_2} />
            <Detail label="When" value={DATE_LONG} />
            <Detail label="Where" value={ADDR} />
          </div>
          <EmailCta href={MANAGE_URL}>View booking</EmailCta>
        </EmailFrame>
      ),
    },
  ];

  const smsItems: NotifItem[] = [
    {
      id: 'sms-booking-confirmation',
      title: 'Booking confirmation',
      trigger: 'When a client books',
      preview: `${SERVICE} confirmed for ${DATE_SHORT}`,
      tier: 'small_plus',
      body: (
        <SmsMock
          lines={[
            `Hi ${CLIENT} — your ${SERVICE} with ${BNAME} is confirmed for ${DATE_SHORT}.\n\nManage: ${MANAGE_URL}`,
          ]}
        />
      ),
    },
    {
      id: 'sms-reminder-24h',
      title: '24-hour reminder',
      trigger: 'The day before',
      preview: `Reminder: ${SERVICE} tomorrow at 09:30`,
      tier: 'small_plus',
      body: (
        <SmsMock
          lines={[
            `Reminder: ${SERVICE} tomorrow at 09:30 — ${BNAME}.\n\nNeed to reschedule? ${MANAGE_URL}`,
          ]}
        />
      ),
    },
    {
      id: 'sms-reminder-1h',
      title: '1-hour reminder',
      trigger: 'An hour before',
      preview: 'Starting in 1 hour',
      tier: 'large_only',
      body: (
        <SmsMock
          lines={[
            `Starting in 1 hour: ${SERVICE} at 09:30 — ${BNAME}.\n\n${MANAGE_URL}`,
          ]}
        />
      ),
    },
    {
      id: 'sms-rescheduled',
      title: 'Rescheduled',
      trigger: 'When a booking is moved',
      preview: `Moved to ${DATE_NEW_SHORT}`,
      tier: 'large_only',
      body: (
        <SmsMock
          lines={[
            `Your ${SERVICE} has been moved to ${DATE_NEW_SHORT} — ${BNAME}.\n\nManage: ${MANAGE_URL}`,
          ]}
        />
      ),
    },
    {
      id: 'sms-cancelled',
      title: 'Cancelled',
      trigger: 'When a booking is cancelled',
      preview: `Your ${SERVICE} on ${DATE_SHORT} has been cancelled`,
      tier: 'large_only',
      body: (
        <SmsMock
          lines={[
            `Your ${SERVICE} on ${DATE_SHORT} with ${BNAME} has been cancelled.\n\nRebook: ${MANAGE_URL}`,
          ]}
        />
      ),
    },
    {
      id: 'sms-staff-assigned',
      title: 'New staff assigned',
      trigger: 'After a sick-day reshuffle',
      preview: `Now with ${STAFF_2}`,
      tier: 'large_only',
      body: (
        <SmsMock
          lines={[
            `${STAFF_1} is unavailable. Your ${SERVICE} (${DATE_SHORT}) is now with ${STAFF_2} — ${BNAME}.\n\n${MANAGE_URL}`,
          ]}
        />
      ),
    },
    {
      id: 'sms-waitlist',
      title: 'Waitlist — slot opened',
      trigger: 'When a spot frees up on a full day',
      preview: `A ${SERVICE} slot opened on ${DATE_SHORT}`,
      tier: 'large_only',
      body: (
        <SmsMock
          lines={[
            `Good news, ${CLIENT} — a ${SERVICE} slot opened at ${BNAME} on ${DATE_SHORT}.\n\nBook now (first in): ${MANAGE_URL}`,
          ]}
        />
      ),
    },
  ];

  return (
    <>
      {/* HERO */}
      <section className="bg-brand-cream">
        <div className="mx-auto max-w-6xl px-6 pb-16 pt-14 sm:px-12 sm:pt-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-cream-shadow px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage ring-1 ring-brand-sage/15">
            <Mail className="h-4 w-4" />
            Notifications
          </div>
          <h1 className="mt-6 max-w-3xl text-4xl font-extrabold leading-[1.05] tracking-tightest text-brand-ink-soft sm:text-6xl">
            Every notification,{' '}
            <span className="text-brand-sage">exactly as clients see it.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-brand-ink-soft/75 sm:text-xl">
            From booking confirmation to the 1-hour reminder, here&rsquo;s
            what Bookaburra sends on your behalf. Names and dates below are
            examples — the real messages use your client&rsquo;s name and
            their actual booking details.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href="#emails"
              className="inline-flex items-center gap-2 rounded-full bg-brand-cream-shadow px-4 py-2 text-sm font-semibold text-brand-sage ring-1 ring-brand-sage/20 hover:bg-brand-sage/10"
            >
              <Mail className="h-4 w-4" />
              Emails
            </a>
            <a
              href="#sms"
              className="inline-flex items-center gap-2 rounded-full bg-brand-cream-shadow px-4 py-2 text-sm font-semibold text-brand-sage ring-1 ring-brand-sage/20 hover:bg-brand-sage/10"
            >
              <MessageSquareText className="h-4 w-4" />
              Text messages
            </a>
          </div>
        </div>
      </section>

      {/* EMAILS */}
      <section id="emails" className="bg-brand-cream-shadow">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:px-12 sm:py-20">
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-cream px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage ring-1 ring-brand-sage/15">
              <Mail className="h-3.5 w-3.5" />
              Emails
            </div>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-4xl">
              Emails your clients receive
            </h2>
            <p className="mt-4 max-w-2xl text-brand-ink-soft/70">
              All plans include email notifications. Replies land directly in
              your business inbox — not a no-reply address.
            </p>
          </div>

          <NotifSplitView items={emailItems} sectionId="emails" />
        </div>
      </section>

      {/* SMS */}
      <section id="sms" className="bg-brand-cream">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:px-12 sm:py-20">
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-cream-shadow px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage ring-1 ring-brand-sage/15">
              <MessageSquareText className="h-3.5 w-3.5" />
              Text messages
            </div>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-4xl">
              SMS notifications
            </h2>
            <p className="mt-4 max-w-2xl text-brand-ink-soft/70">
              Text messages are short, direct, and always include a single
              management link. Small business plans include booking confirmation
              and the 24-hour reminder. Large business plans unlock all 12
              notification types.
            </p>
          </div>

          <NotifSplitView items={smsItems} sectionId="sms" />
        </div>
      </section>

      {/* PRIVACY — what we don't store */}
      <section className="bg-brand-cream-shadow">
        <div className="mx-auto max-w-6xl px-6 py-14 sm:px-12 sm:py-16">
          <div className="mx-auto max-w-3xl rounded-2xl border border-brand-sage/15 bg-white p-6 shadow-[0_30px_80px_-30px_rgba(19,33,36,0.06)] sm:p-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-cream-shadow px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage ring-1 ring-brand-sage/15">
              What we don&rsquo;t store
            </div>
            <p className="mt-4 text-brand-ink-soft/80">
              We send these notifications and immediately forget the contents.
              Bookaburra logs <em>that</em> a notification was sent (which
              client, which template, when) — never the rendered text or the
              recipient&rsquo;s reply. Inbound SMS and email replies go straight
              to the business owner&rsquo;s inbox; we don&rsquo;t index, store,
              or analyse them.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-ink text-brand-cream">
        <div className="mx-auto max-w-6xl px-6 py-20 sm:px-12 sm:py-24">
          <div className="grid gap-10 lg:grid-cols-[3fr_2fr] lg:items-center">
            <div>
              <h2 className="text-3xl font-extrabold tracking-tight sm:text-5xl">
                On Large business, edit every word.
              </h2>
              <p className="mt-6 max-w-xl text-lg text-brand-cream/80">
                Change the subject line, the greeting, the sign-off. Every
                template in every channel is yours to customise — with a live
                preview and an SMS character-budget indicator.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <a
                href={signupUrl()}
                className="inline-flex items-center justify-center rounded-md bg-brand-cream px-6 py-3.5 text-sm font-medium text-brand-ink-soft hover:bg-brand-cream/90"
              >
                Start for free
              </a>
              <a
                href="/pricing"
                className="inline-flex items-center justify-center rounded-md border border-brand-cream/30 px-6 py-3.5 text-sm font-medium text-brand-cream hover:bg-brand-cream/10"
              >
                See pricing
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
